import React from 'react';
import PropTypes from 'prop-types';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {withFlexHolder} from '@/hocs/withFlex';
import {withMarginBottomMd} from '@/hocs/withSpacing';
import {
  SectionPaddingBottom,
  SectionPaddingTop,
  SectionPadding,
  SectionPaddingHero
} from '@/section';
import {withHomeSubP} from '@/hocs/withFontType';
import Container from '@/container';
import IntroBox from '@/ui/introbox';
import {H2, P, H1, H5Neutral900Light} from '@/typography';
import Link from '@/links';
import Img from '@/images';
import {withBgPurple50} from '@/hocs/withColors';
import HeroBannerFreeToolsForManagers from '@/ui/hero-banner/free-tools-for-managers';

// Shapes
import HiringEasierDotsShape from '@/ui/decorative/free-tools-for-managers/hiring-easier-dots-shape';
import HiringEasierBottomShape from '@/ui/decorative/free-tools-for-managers/hiring-easier-bottom-shape';

// Content imports
import {BreakVisibleInDesktop, BreakHiddenDesktop} from '@/breaks';

// Form specific assets
const FlexHolderRow = withFlexHolder(Row);
const DivMargined = withMarginBottomMd('div');
const SectionPurple50Padding = withBgPurple50(SectionPadding);
const PSub = withHomeSubP(P);

const seoMetas = {
  title: 'Free Tools for Managers - The Hiring Help You Need',
  description:
    'Workable’s Free Tools for Managers: A free hiring tool for managers. Create job descriptions, easily prep interviews & evaluate candidates. Try now!',
  canonical: 'https://www.workable.com/free-tools-for-managers'
};

const propTypes = {
  position: PropTypes.string
};

const TryToolsForFreeCta = ({position = 'body'}) => (
  <Link
    href="https://tools.workable.com"
    target="_blank"
    hasPathAsQueryParam
    variant="secondary-sm"
    customAttributes={{'data-gtm': `generic-${position}-free-hiring-tools`}}>
    TRY THE TOOLS FOR FREE
  </Link>
);

TryToolsForFreeCta.propTypes = propTypes;

const FreeToolsForManagersPage = () => (
  <>
    <SectionPaddingBottom>
      <HeroBannerFreeToolsForManagers />

      <SectionPaddingHero>
        <Container variant="section-padding-below-nav">
          <Grid fluid>
            <Row start="lg" middle="lg">
              <Col xs={12} lg={6}>
                <H5Neutral900Light>
                  Workable’s Free Tools <BreakHiddenDesktop /> for Managers
                </H5Neutral900Light>

                <H1>The hiring tools you need</H1>

                <div>
                  <PSub>The best way to evaluate candidates, for free</PSub>

                  <P>
                    Finding the right people for your team shouldn't be a struggle. Workable offers
                    free hiring tools for managers who want a straightforward, easy-to-use system to
                    help them hire.
                    <BreakVisibleInDesktop />
                    Utilizing AI, Workable’s Free Tools for Managers make creating interview
                    questions and evaluating candidates a breeze. Plus, you can manage several jobs
                    at once, all within Workable.
                  </P>

                  <TryToolsForFreeCta position="hero" />
                </div>
              </Col>
            </Row>
          </Grid>
        </Container>
      </SectionPaddingHero>
    </SectionPaddingBottom>

    <SectionPaddingBottom>
      <Container>
        <Grid fluid>
          <FlexHolderRow top="xs">
            <Col xs={12} lg={6}>
              <DivMargined>
                <picture>
                  <source
                    srcSet="/static/images/free-tools-for-managers/generate-interview-kit-desktop.webp, /static/images/free-tools-for-managers/generate-interview-kit-desktop_2x.webp 2x"
                    type="image/webp"
                  />
                  <Img
                    srcSet="/static/images/free-tools-for-managers/generate-interview-kit-desktop.png 1x, /static/images/free-tools-for-managers/generate-interview-kit-desktop_2x.png 2x"
                    src="/static/images/free-tools-for-managers/generate-interview-kit-desktop_2x.png"
                    type="image/png"
                    alt="Workable graphic of Generate an interview kit"
                  />
                </picture>
              </DivMargined>
            </Col>
            <Col xs={12} lg={5} lgOffset={1}>
              <IntroBox>
                <H2>
                  Generate an <BreakVisibleInDesktop /> interview kit
                </H2>
                <P>
                  Simply type in your job title, choose your industry and tone, and you’ll get a
                  tailored interview kit in seconds. This includes welcoming and closing questions,
                  core questions, and follow up questions. Asking the right questions is crucial to
                  finding the right person, so you can create an account to regenerate and edit
                  specific questions or change the tone – and save your interview kit for later use.
                </P>

                <TryToolsForFreeCta />
              </IntroBox>
            </Col>
          </FlexHolderRow>
        </Grid>
      </Container>
    </SectionPaddingBottom>
    <SectionPaddingBottom>
      <Container>
        <Grid fluid>
          <FlexHolderRow top="xs">
            <Col xs={12} lg={5}>
              <IntroBox>
                <H2>Evaluate candidates</H2>
                <P>
                  Once you have your interview questions, upload your candidates by typing in their
                  email address. Workable will fetch their publicly available information and build
                  them a full profile in seconds. Then, you’re able to use their profile to ask them
                  your generated interview questions and fill out their interview kit as you go, all
                  within Workable.
                </P>

                <TryToolsForFreeCta />
              </IntroBox>
            </Col>
            <Col xs={12} lg={6} lgOffset={1} first="xs" last="lg">
              <DivMargined>
                <picture>
                  <source
                    srcSet="/static/images/free-tools-for-managers/evaluate-candidates-desktop.webp, /static/images/free-tools-for-managers/evaluate-candidates-desktop_2x.webp 2x"
                    type="image/webp"
                  />
                  <Img
                    srcSet="/static/images/free-tools-for-managers/evaluate-candidates-desktop.png 1x, /static/images/free-tools-for-managers/evaluate-candidates-desktop_2x.png 2x"
                    src="/static/images/free-tools-for-managers/evaluate-candidates-desktop_2x.png"
                    type="image/png"
                    alt="Workable graphic for Evaluate candidates"
                  />
                </picture>
              </DivMargined>
            </Col>
          </FlexHolderRow>
        </Grid>
      </Container>
    </SectionPaddingBottom>
    <SectionPaddingBottom>
      <Container>
        <Grid fluid>
          <FlexHolderRow top="xs">
            <Col xs={12} lg={6}>
              <DivMargined>
                <picture>
                  <source
                    srcSet="/static/images/free-tools-for-managers/job-description-generation-desktop.webp, /static/images/free-tools-for-managers/job-description-generation-desktop_2x.webp 2x"
                    type="image/webp"
                  />
                  <Img
                    srcSet="/static/images/free-tools-for-managers/job-description-generation-desktop.png 1x, /static/images/free-tools-for-managers/job-description-generation-desktop_2x.png 2x"
                    src="/static/images/free-tools-for-managers/job-description-generation-desktop_2x.png"
                    type="image/png"
                    alt="Workable graphic of Job description generation"
                  />
                </picture>
              </DivMargined>
            </Col>
            <Col xs={12} lg={5} lgOffset={1}>
              <IntroBox>
                <H2>Job description generation</H2>
                <P>
                  Our AI generated job descriptions are tailored for your role, including relevant
                  content for your industry. Workable has seen millions of job descriptions, so we
                  know what works. Boost your reach with inclusive language and SEO optimization to
                  drive more applications.
                </P>

                <TryToolsForFreeCta />
              </IntroBox>
            </Col>
          </FlexHolderRow>
        </Grid>
      </Container>
    </SectionPaddingBottom>
    <SectionPaddingBottom>
      <Container>
        <Grid fluid>
          <FlexHolderRow top="xs" last="xs" first="lg">
            <Col xs={12} lg={5}>
              <IntroBox>
                <H2>Organize your hiring</H2>
                <P>
                  Workable’s Free Tools for Managers help you organize your hiring by keeping
                  multiple jobs in the system at once. Your job descriptions, interview questions,
                  and candidates all in one place.
                </P>

                <TryToolsForFreeCta />
              </IntroBox>
            </Col>
            <Col xs={12} lg={6} lgOffset={1} first="xs" last="lg">
              <DivMargined>
                <picture>
                  <source
                    srcSet="/static/images/free-tools-for-managers/organize-your-hiring-desktop.webp, /static/images/free-tools-for-managers/organize-your-hiring-desktop_2x.webp 2x"
                    type="image/webp"
                  />
                  <Img
                    srcSet="/static/images/free-tools-for-managers/organize-your-hiring-desktop.png 1x, /static/images/free-tools-for-managers/organize-your-hiring-desktop_2x.png 2x"
                    src="/static/images/free-tools-for-managers/organize-your-hiring-desktop_2x.png"
                    type="image/png"
                    alt="Workable graphic of Organize your hiring"
                  />
                </picture>
              </DivMargined>
            </Col>
          </FlexHolderRow>
        </Grid>
      </Container>
    </SectionPaddingBottom>

    <SectionPaddingTop>
      <SectionPurple50Padding>
        <Container>
          <HiringEasierDotsShape />

          <Grid fluid>
            <Row center="xs">
              <Col xs={12} md={8}>
                <H2>We’re here to make hiring easier</H2>
              </Col>
            </Row>
            <Row center="xs">
              <Col xs={12} md={6}>
                <P>
                  Evaluating candidates can be a challenge, especially for small businesses with
                  limited time and resources.
                  <BreakVisibleInDesktop />
                  <BreakVisibleInDesktop />
                  Workable’s Free Tools for Managers are here to help you find and hire the right
                  people with less stress.
                </P>

                <TryToolsForFreeCta />
              </Col>
            </Row>
          </Grid>
          <HiringEasierBottomShape />
        </Container>
      </SectionPurple50Padding>
    </SectionPaddingTop>
  </>
);

export default withMainLayout(FreeToolsForManagersPage, {...seoMetas});

export {getSharedPageStaticProps as getStaticProps} from 'helpers/sharedPageStaticProps';
